import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { productUrl } from "@/config/siteUrls";

const METHODOLOGY_LINKS = [
  { label: "Overview", to: "/methodology", desc: "The methodology at a glance." },
  { label: "Why Adaptive Physiology?", to: "/methodology/why", desc: "The gap in behavior change — and how physiology closes it." },
  { label: "The Framework", to: "/methodology/framework", desc: "The four pillars of the protocol." },
  { label: "The Science", to: "/methodology/science", desc: "Research and physiological foundations." },
  { label: "How It Works", to: "/methodology/how-it-works", desc: "The closed-loop protocol in practice." },
  { label: "Outcomes", to: "/methodology/outcomes", desc: "What the methodology measurably shifts." },
];

const NAV_ITEMS = [
  { key: "methodology", label: "Methodology", type: "dropdown", items: METHODOLOGY_LINKS },
  { key: "applications", label: "Applications", type: "link", to: "/applications" },
  { key: "programs", label: "Programs", type: "link", to: "/programs" },
  { key: "foundations", label: "Foundations", type: "link", to: "/foundations" },
  { key: "assessment", label: "Assessment", type: "link", to: "/assessment" },
  { key: "companion", label: "Companion", type: "link", to: "/app" },
  { key: "about", label: "About", type: "link", to: "/about/origin" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [pathname]);

  const opaqueHeader = scrolled || pathname !== "/";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        opaqueHeader
          ? "backdrop-blur-xl border-b"
          : "bg-transparent border-b border-transparent"
      }`}
      style={
        opaqueHeader
          ? {
              backgroundColor: "rgba(247, 245, 240, 0.86)",
              borderColor: "var(--forr-line-soft)",
            }
          : undefined
      }
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Link
          data-testid="header-logo"
          to="/"
          className="flex items-center gap-2.5 group"
        >
          <div className="relative w-8 h-8">
            <div className="absolute inset-0 rounded-full bg-teal-400/25 blur-md group-hover:bg-teal-400/50 transition-colors" />
            <div className="absolute inset-[2px] rounded-full border" style={{ borderColor: "var(--forr-teal)" }} />
            <div className="absolute inset-[8px] rounded-full" style={{ backgroundColor: "var(--forr-teal)" }} />
          </div>
          <span className="flex flex-col leading-none">
            <span className="font-display text-[15px] font-medium tracking-tight forr-text-ink">
              Adaptive Physiology
              <span className="forr-text-teal-deep align-super text-[9px] ml-0.5">™</span>
            </span>
            <span className="text-[10px] tracking-[0.22em] uppercase forr-text-mute mt-1">
              by FORR Health
            </span>
          </span>
        </Link>

        <nav ref={dropdownRef} className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((n) =>
            n.type === "dropdown" ? (
              <div key={n.key} className="relative">
                <button
                  data-testid={`nav-${n.key}`}
                  onClick={() =>
                    setOpenDropdown((v) => (v === n.key ? null : n.key))
                  }
                  onMouseEnter={() => setOpenDropdown(n.key)}
                  className={`flex items-center gap-1.5 text-sm tracking-wide transition-colors ${
                    openDropdown === n.key
                      ? "forr-text-ink"
                      : "forr-text-soft hover:forr-text-ink"
                  }`}
                >
                  {n.label}
                  <ChevronDown
                    size={13}
                    strokeWidth={1.75}
                    className={`transition-transform duration-300 ${
                      openDropdown === n.key ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <AnimatePresence>
                  {openDropdown === n.key && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      onMouseLeave={() => setOpenDropdown(null)}
                      data-testid={`nav-${n.key}-dropdown`}
                      className="absolute left-1/2 -translate-x-1/2 top-full mt-4 w-[480px] rounded-[20px] p-3"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.98)",
                        border: "1px solid var(--forr-line)",
                        boxShadow: "var(--forr-shadow-lg)",
                      }}
                    >
                      {n.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          data-testid={`nav-${n.key}-${item.to.split("/").pop()}`}
                          className="block rounded-[14px] p-4 hover:bg-black/[0.03] transition-colors group"
                        >
                          <div className="forr-text-ink text-sm font-medium tracking-tight group-hover:forr-text-teal-deep transition-colors">
                            {item.label}
                          </div>
                          <div className="mt-1 text-[13px] forr-text-mute font-light leading-snug">
                            {item.desc}
                          </div>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : n.key === "assessment" || n.key === "companion" ? (
              <a
                key={n.key}
                data-testid={`nav-${n.key}`}
                href={productUrl(n.to)}
                className="text-sm forr-text-soft hover:forr-text-ink transition-colors tracking-wide"
              >
                {n.label}
              </a>
            ) : (
              <Link
                key={n.key}
                data-testid={`nav-${n.key}`}
                to={n.to}
                className="text-sm forr-text-soft hover:forr-text-ink transition-colors tracking-wide"
              >
                {n.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex flex-col items-center gap-1">
          <a
            data-testid="header-cta"
            href={productUrl("/assessment/start")}
            className="forr-btn-primary inline-flex text-sm font-medium px-5 py-2 rounded-full"
          >
            Take the assessment
          </a>
          <a
            data-testid="header-coaching"
            href={productUrl("/coaching")}
            className="text-[11px] font-medium forr-text-teal-deep hover:underline"
          >
            1-to-1 coaching
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden forr-text-ink p-2"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? (
            <X size={22} strokeWidth={1.5} />
          ) : (
            <Menu size={22} strokeWidth={1.5} />
          )}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden forr-canvas border-b max-h-[80vh] overflow-y-auto"
            style={{ borderColor: "var(--forr-line-soft)" }}
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              <div className="text-[10px] uppercase tracking-[0.28em] forr-text-mute mt-2 mb-1">
                Methodology
              </div>
              {METHODOLOGY_LINKS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="forr-text-ink text-base py-2 border-b"
                  style={{ borderColor: "var(--forr-line-soft)" }}
                >
                  {item.label}
                </Link>
              ))}
              <div className="text-[10px] uppercase tracking-[0.28em] forr-text-mute mt-6 mb-1">
                More
              </div>
              <Link
                to="/applications"
                className="forr-text-ink text-base py-2 border-b"
                style={{ borderColor: "var(--forr-line-soft)" }}
              >
                Applications
              </Link>
              <Link
                to="/programs"
                className="forr-text-ink text-base py-2 border-b"
                style={{ borderColor: "var(--forr-line-soft)" }}
              >
                Programs
              </Link>
              <Link
                to="/foundations"
                className="forr-text-ink text-base py-2 border-b"
                style={{ borderColor: "var(--forr-line-soft)" }}
              >
                Scientific Foundations
              </Link>
              <a
                href={productUrl("/assessment")}
                className="forr-text-ink text-base py-2 border-b"
                style={{ borderColor: "var(--forr-line-soft)" }}
              >
                Adaptive Assessment
              </a>
              <a
                href={productUrl("/app")}
                className="forr-text-ink text-base py-2 border-b"
                style={{ borderColor: "var(--forr-line-soft)" }}
              >
                Adaptive Companion
              </a>
              <Link
                to="/about/origin"
                className="forr-text-ink text-base py-2 border-b"
                style={{ borderColor: "var(--forr-line-soft)" }}
              >
                About
              </Link>
              <a
                href={productUrl("/assessment/start")}
                data-testid="mobile-cta"
                className="forr-btn-primary text-sm font-medium px-5 py-3 rounded-full mt-6 inline-flex justify-center"
              >
                Take the assessment
              </a>
              <a
                href={productUrl("/coaching")}
                data-testid="mobile-coaching"
                className="forr-btn-secondary text-sm font-medium px-5 py-3 rounded-full inline-flex justify-center"
              >
                1-to-1 coaching
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
